// @ts-nocheck
/**
 * Contact Groups list — redesigned to obd3-gui pattern (FillPage + PageHeader
 * + DataTable + shadcn Dialog for Create + ConfirmDialog for Delete).
 * Preserves groupNameRegex (alnum + space + common punctuation) client-side validation, ACL gates,
 * v1 bug where the Updated At cell renders created_at.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { Download, Loader2, Plus, Trash2, Users, FolderTree } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import Loader from '@/components/partials/components/Loader';
import { getApiCall, postApiCall } from '@/service/httpCall';
import {
  API_CONTACT_GROUP_CREATE,
  API_CONTACT_GROUP_LIST,
  API_CONTACT_GROUP_DELETE,
} from '@/service/url';
import { useAuth, errorCheck } from '@/views/dashboard/auth/auth-utils';
import { getUser } from '@/utilities/setting';
import { USER_ROLES, MODULE_ACL } from '@/utilities/constant';
import { exportToCSV, hasPermission } from '@/utilities/helpers';
import { cn } from '@/lib/utils/cn';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';

const groupNameRegex = /^[a-zA-Z0-9 .,:·\-_()&/#@]*$/;

const formatDate = (v: any) =>
  v
    ? new Date(v).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    : '—';

const ContactGroupList = () => {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [searchValue, setSearchValue] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastProps, setToastProps] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<any>(null);
  const [deleting, setDeleting] = useState(false);
  const [newGroup, setNewGroup] = useState<any>({ name: '' });
  const [loggedInUserACL, setLoggedInUserACL] = useState<any>({});
  const [isSuperAdmin, setIsSuperadmin] = useState(false);
  const [groupsData, setGroupsData] = useState<any[]>([]);
  const [moduleOperationsForReadPermissionCheck, setModuleOperationsForReadPermissionCheck] =
    useState<any>({});

  useEffect(() => {
    checkAuth(setToastProps, setShowToast);
  }, []);

  useEffect(() => {
    setLoading(true);
    getApiCall(API_CONTACT_GROUP_LIST, {})
      .then((resp: any) => setGroupsData(resp || []))
      .catch(onError)
      .finally(() => setLoading(false));
    const user = getUser();
    if (user.role === USER_ROLES.SUPERADMIN) setIsSuperadmin(true);
    if (user?.acl_settings) {
      const parsed = parseModuleOperations(user.acl_settings.module_operations);
      setLoggedInUserACL(parsed);
      setModuleOperationsForReadPermissionCheck(parsed);
    }
  }, []);

  useEffect(() => {
    const onAclRefreshed = () => {
      const user = getUser();
      if (user?.acl_settings?.module_operations) {
        try {
          const parsed =
            typeof user.acl_settings.module_operations === 'string'
              ? parseModuleOperations(user.acl_settings.module_operations)
              : user.acl_settings.module_operations;
          setLoggedInUserACL(parsed);
          setModuleOperationsForReadPermissionCheck(parsed);
        } catch (e) {
          console.log('Failed to apply refreshed ACL', e);
        }
      }
    };
    window.addEventListener('acl-refreshed', onAclRefreshed);
    return () => window.removeEventListener('acl-refreshed', onAclRefreshed);
  }, []);

  const refetch = () =>
    getApiCall(API_CONTACT_GROUP_LIST, {})
      .then((resp: any) => setGroupsData(resp || []))
      .catch(onError);

  function onError(err: any) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      console.log(err);
    }
  }

  const isContactsRole = getUser()?.role === USER_ROLES.CALL_CENTRE_ADMIN;
  const canRead =
    isContactsRole &&
    hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.CONTACT_GROUP, 'read');
  const canAdd =
    isContactsRole &&
    hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.CONTACT_GROUP, 'add');
  const canDelete =
    isContactsRole &&
    hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.CONTACT_GROUP, 'delete');

  // DataTable's built-in globalFilter handles name / creator / id search.
  const filteredGroups = groupsData;

  const handleCreateGroup = () => {
    const trimmedName = newGroup.name.trim();
    if (!trimmedName) {
      setValidationError('Group name cannot be empty');
      return;
    }
    setValidationError('');
    setSaving(true);
    postApiCall(API_CONTACT_GROUP_CREATE, { group_name: trimmedName })
      .then(() => {
        toast.success('Contact Group created successfully');
        setShowCreateModal(false);
        setNewGroup({ name: '' });
        refetch();
      })
      .catch((err: any) => {
        if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
          errorCheck(err, navigate, setToastProps, setShowToast);
        } else {
          toast.error(friendlyApiError(err) || 'Failed to create group');
        }
      })
      .finally(() => setSaving(false));
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await postApiCall(API_CONTACT_GROUP_DELETE.replace(':id', pendingDelete.id), {});
      toast.success('Contact Group deleted successfully');
      setPendingDelete(null);
      await refetch();
    } catch (err: any) {
      toast.error(friendlyApiError(err) || 'Error deleting group');
    } finally {
      setDeleting(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      exportToCSV({
        fileName: 'contact-groups-list',
        data: filteredGroups,
        columns: [
          { header: 'Group Id', accessor: 'id' },
          { header: 'Group Name', accessor: (row: any) => row.group_name || '-' },
          { header: 'Members', accessor: (row: any) => row.member_count || 0 },
          {
            header: 'Created By',
            accessor: (row: any) => row.group_creator?.username || '-',
          },
          { header: 'Created At', accessor: (row: any) => formatDate(row.created_at) },
          { header: 'Updated At', accessor: (row: any) => formatDate(row.updated_at) },
        ],
      });
    } finally {
      setExporting(false);
    }
  };

  const columns = useMemo<ColumnDef<any>[]>(() => {
    const cols: ColumnDef<any>[] = [
      {
        accessorKey: 'id',
        header: 'ID',
        size: 80,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground tabular-nums">
            #{row.original.id}
          </span>
        ),
      },
      {
        accessorKey: 'group_name',
        header: 'Group Name',
        size: 260,
        cell: ({ row }) => {
          const nm = row.original.group_name || '—';
          const initials = (nm || '')
            .split(/\s+/)
            .filter(Boolean)
            .slice(0, 2)
            .map((w: string) => w[0])
            .join('')
            .toUpperCase() || '—';
          return (
            <div className="flex items-center gap-2.5">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-semibold text-primary ring-1 ring-inset ring-primary/20">
                {initials}
              </span>
              <Link
                to={`/contact-group/${row.original.id}`}
                state={{ group: row.original }}
                className="font-medium text-foreground hover:text-primary hover:underline"
              >
                {nm}
              </Link>
            </div>
          );
        },
      },
      {
        accessorKey: 'member_count',
        header: 'Members',
        size: 110,
        cell: ({ row }) => (
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary/10 px-2.5 py-0.5 text-[11px] font-semibold text-primary ring-1 ring-inset ring-primary/20">
            <Users className="h-3 w-3" />
            <span className="tabular-nums">{row.original.member_count || 0}</span>
          </span>
        ),
      },
      {
        accessorFn: (row: any) => row.group_creator?.username,
        id: 'group_creator',
        header: 'Created By',
        cell: ({ row }) => (
          <span className="text-sm text-foreground">
            {row.original.group_creator?.username || '—'}
          </span>
        ),
      },
      {
        accessorKey: 'created_at',
        header: 'Created',
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-xs text-muted-foreground tabular-nums">
            {formatDate(row.original.created_at)}
          </span>
        ),
      },
      {
        accessorKey: 'updated_at',
        header: 'Updated',
        // v1 bug preserved: cell renders created_at, not updated_at
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-xs text-muted-foreground tabular-nums">
            {formatDate(row.original.created_at)}
          </span>
        ),
      },
    ];

    if (canDelete) {
      cols.push({
        id: 'actions',
        header: 'Actions',
        size: 100,
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) =>
          canDelete ? (
            <button
              type="button"
              title={`Delete ${row.original.group_name}`}
              onClick={() => setPendingDelete(row.original)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-destructive/10 text-destructive transition-colors hover:bg-destructive/15"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          ) : null,
      });
    }

    return cols;
  }, [canDelete]);

  // Stat strip data
  const totalGroups = groupsData.length;
  const totalMembers = groupsData.reduce(
    (sum: number, g: any) => sum + (Number(g.member_count) || 0),
    0,
  );

  if (!canRead) {
    return (
      <FillPage>
        <PageHeader title="Contact Groups" />
        <div className="rounded-md border border-border bg-muted/30 p-8 text-center text-sm text-muted-foreground">
          No read permission found in assigned ACL.
        </div>
      </FillPage>
    );
  }

  if (loading && groupsData.length === 0) {
    return (
      <FillPage>
        <Loader />
      </FillPage>
    );
  }

  return (
    <FillPage>
      <PageHeader
        title="Contact Groups"
        description="Grouped phone books used to feed campaigns and lead assignments."
        actions={
          <>
            {groupsData.length > 0 && (
              <Button variant="outline" onClick={handleExport} disabled={exporting}>
                <Download className="h-4 w-4" />
                Export
              </Button>
            )}
            {canAdd && (
              <Button onClick={() => setShowCreateModal(true)}>
                <Plus className="h-4 w-4" />
                New Group
              </Button>
            )}
          </>
        }
      />

      {/* Stat strip — Total groups / Total members */}
      <div className="mb-3 flex flex-wrap gap-2">
        <div className="flex items-center gap-2 rounded-full border border-border/60 bg-card px-3 py-1.5 text-xs shadow-neu-raised">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary">
            <FolderTree className="h-3.5 w-3.5" />
          </span>
          <span className="text-muted-foreground">Total groups</span>
          <span className="font-semibold text-foreground tabular-nums">{totalGroups}</span>
        </div>
        <div className="flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-3 py-1.5 text-xs shadow-neu-raised dark:border-sky-900/60 dark:bg-sky-950/30">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-sky-100 text-sky-700 dark:bg-sky-900/60 dark:text-sky-300">
            <Users className="h-3.5 w-3.5" />
          </span>
          <span className="text-sky-700 dark:text-sky-300">Total members</span>
          <span className="font-semibold text-sky-800 tabular-nums dark:text-sky-200">
            {totalMembers}
          </span>
        </div>
      </div>

      <DataTable
        tableId="contact-group.list:v1"
        columns={columns}
        data={filteredGroups}
        isLoading={loading}
        searchPlaceholder={
          isSuperAdmin
            ? 'Search by group name, ID, or creator…'
            : 'Search by group name or ID…'
        }
        emptyTitle="No contact groups"
        emptyDescription="Create a group to start uploading contacts."
        getRowId={(row: any) => String(row.id)}
        initialColumnPinning={canDelete ? { right: ['actions'] } : undefined}
        pageSize={10}
        disableExport
      />

      {/* Create modal */}
      <Dialog
        open={showCreateModal}
        onOpenChange={(o) => {
          if (!o) {
            setShowCreateModal(false);
            setNewGroup({ name: '' });
            setValidationError('');
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-4 w-4 text-primary" />
              New contact group
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="group_name">
              Group name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="group_name"
              type="text"
              value={newGroup.name}
              onChange={(e) => {
                if (!groupNameRegex.test(e.target.value)) return;
                setNewGroup({ name: e.target.value });
                if (validationError) setValidationError('');
              }}
              placeholder="Enter group name (letters, digits, spaces, . , : - _ ( ) & / # @)"
              maxLength={100}
              className={cn(
                validationError && 'border-destructive focus-visible:ring-destructive',
              )}
            />
            {validationError && (
              <p className="text-xs text-destructive">{validationError}</p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateModal(false);
                setNewGroup({ name: '' });
                setValidationError('');
              }}
            >
              Discard
            </Button>
            <Button onClick={handleCreateGroup} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {saving ? 'Creating…' : 'Create group'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Delete this contact group?"
        description={
          <>
            Group{' '}
            <span className="font-medium text-foreground">
              {pendingDelete?.group_name}
            </span>{' '}
            and all its contacts will be permanently deleted.
          </>
        }
        confirmLabel="Delete"
        tone="destructive"
        loading={deleting}
        onConfirm={confirmDelete}
      />
    </FillPage>
  );
};

export default ContactGroupList;
